import type { Framing } from '../components/StudioImage'

export type MediaAsset = {
  id: string
  fileName: string
  contentType: string
  sizeBytes: number
  altText: string | null
  caption: string | null
  uploadedAt: string
  url: string
  /** How it sits in a box, unless a placement overrides it (D-199). */
  framing: Framing
}

export type PlacementFraming = { placement: string; framing: Framing }

export type FramingView = {
  default: Framing
  /** Places where somebody has already made an exception. */
  overrides: PlacementFraming[]
}

export async function fetchFraming(assetId: string): Promise<FramingView> {
  const res = await fetch(`/api/admin/media/${assetId}/framing`)
  if (!res.ok) throw new Error(`Could not load the framing (${res.status})`)
  return res.json()
}

/**
 * Saves framing for a photograph, or for one place it appears.
 *
 * No `placement` means the photograph's own default — right almost everywhere,
 * because `cover` keeps the focal point in frame at any shape. With one, it is
 * the exception for a box that fights the default.
 */
export async function saveFraming(
  assetId: string,
  framing: Framing,
  placement?: string,
): Promise<void> {
  const query = placement ? `?placement=${encodeURIComponent(placement)}` : ''

  const res = await fetch(`/api/admin/media/${assetId}/framing${query}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(framing),
  })

  if (!res.ok) throw new Error(`Could not save the framing (${res.status})`)
}

/** Returns a placement to the photograph's own framing — not the same as centring it. */
export async function clearFraming(assetId: string, placement: string): Promise<void> {
  const res = await fetch(
    `/api/admin/media/${assetId}/framing?placement=${encodeURIComponent(placement)}`,
    { method: 'DELETE' },
  )

  if (!res.ok) throw new Error(`Could not reset the framing (${res.status})`)
}

/** The placement key for a kind of class (D-191), matching the server's. */
export const classTypePlacement = (classTypeId: string) => `classType:${classTypeId}`

/** The placement key for a content block, matching the server's. */
export const contentPlacement = (blockKey: string) => `content:${blockKey}`

export async function fetchMedia(): Promise<MediaAsset[]> {
  const res = await fetch('/api/admin/media')
  if (!res.ok) throw new Error(`Could not load your photos (${res.status})`)
  return res.json()
}

export type UploadOutcome = {
  uploaded: MediaAsset[]
  /** Per-file reasons, e.g. "IMG_9931.heic is over 8 MB." */
  refused: string[]
}

export async function uploadMedia(files: File[]): Promise<UploadOutcome> {
  const form = new FormData()
  for (const file of files) form.append('files', file)

  const res = await fetch('/api/admin/media', { method: 'POST', body: form })

  if (!res.ok) {
    // The server explains a refusal — too large, or not an image. Passing that
    // through beats "Could not upload that (400)", which tells nobody what to
    // do next.
    const problem = await res.json().catch(() => null)
    throw new Error(problem?.error ?? `Could not upload that (${res.status})`)
  }

  const body = await res.json()

  /*
   * Refusals are carried, not dropped. The endpoint reports them per file
   * *with a reason* rather than skipping — an upload that appears to work and
   * silently does nothing is how a photo goes missing from the site and is
   * only noticed by a visitor.
   */
  return {
    uploaded: body.uploaded ?? body ?? [],
    refused: body.refused ?? [],
  }
}

/**
 * Splits a selection into requests.
 *
 * The studio's archive is 183 files, and a browser sending them as one
 * multipart body is a request measured in hundreds of megabytes — slow to
 * start, impossible to show progress for, and lost entirely if it fails at
 * file 180. Batches give a progress figure that moves and confine a failure
 * to a handful of photographs.
 */
export function batch<T>(items: T[], size: number): T[][] {
  if (size < 1) return items.length ? [items] : []

  const batches: T[][] = []
  for (let i = 0; i < items.length; i += size) batches.push(items.slice(i, i + size))

  return batches
}

/**
 * What to say when a bulk upload finishes.
 *
 * Names the refusals rather than only counting them: "3 were refused" leaves
 * somebody comparing a grid of 183 tiles against a folder to work out which.
 */
export function summariseUpload(uploaded: number, refused: string[]): string {
  const added = uploaded === 1 ? '1 photo added' : `${uploaded} photos added`

  return refused.length === 0 ? `${added}.` : `${added}. ${refused.length} refused.`
}

export async function describeMedia(
  id: string,
  details: { altText: string | null; caption: string | null },
): Promise<void> {
  const res = await fetch(`/api/admin/media/${id}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(details),
  })

  if (!res.ok) throw new Error(`Could not save that (${res.status})`)
}

/**
 * Whether a content block holds a picture rather than words.
 *
 * Decided by the key, not by what is currently in the field. A photo block that
 * has been emptied is still a photo block, and it is exactly then that the
 * editor most needs to offer the picker rather than a text box.
 */
export function isImageBlock(key: string): boolean {
  return key.endsWith('.image') || key.endsWith('.photo') || key === 'site.logo'
    || /\.photo\d+$/.test(key)
}

/** A shape a photograph is previewed in. The ratio only — the caller sizes it. */
export type PlacementShape = { label: string; className: string }

/**
 * Whether framing does anything to this block.
 *
 * <b>The logo is the exception, and it is not an oversight.</b> It renders
 * `object-contain` in both shells — the whole picture, letterboxed, never
 * cropped — so there is nothing for a focal point to keep in frame. Offering
 * the control there would be a control that visibly does nothing, which is
 * worse than not offering it (D-253).
 */
export function isFramedBlock(key: string): boolean {
  return isImageBlock(key) && key !== 'site.logo'
}

/**
 * The shape a content block actually renders at on the public site.
 *
 * <b>A map, and it can drift — so it fails soft.</b> An unrecorded key returns
 * null and the control falls back to its generic shapes, which is what it did
 * before any of this. The alternative, guessing, is how a preview quietly stops
 * matching the page (D-253).
 */
export function placementShape(key: string): PlacementShape | null {
  // AboutView.tsx — owner portraits and the three studio photographs.
  if (/^about\.owner\d+\.photo$/.test(key) || /^about\.photo\d+$/.test(key)) {
    return { label: 'On the page', className: 'aspect-[3/4]' }
  }

  // HomeView.tsx — the gallery strip.
  if (/^home\.gallery\d+\.image$/.test(key)) {
    return { label: 'On the page', className: 'aspect-[4/5]' }
  }

  /*
   * The hero has no single ratio — it is a full-bleed background whose height
   * comes from the headline, so it is a wide band on a desktop and nearly
   * portrait on a phone. Previewed wide because that is where a badly placed
   * focal point actually shows: the phone crop is close to the whole picture.
   */
  if (key === 'home.hero.image') {
    return { label: 'Banner, on a wide screen', className: 'aspect-[16/5]' }
  }

  return null
}

/**
 * A human size. Studio photography off a real camera runs into megabytes, and
 * "4823910" tells nobody whether a page will be slow.
 */
export function fileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`

  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
}

/**
 * The name worth showing a person.
 *
 * Wix exports arrive as `GrpDSC06578_edited_edited.jpg`. Trimming the extension
 * and the editing suffixes leaves something recognisable in a grid of 183.
 */
export function displayName(fileName: string): string {
  const stem = fileName.replace(/\.[a-z0-9]+$/i, '')

  const trimmed = stem
    .replace(/(_edited)+$/i, '')
    .replace(/[-_]+/g, ' ')
    .trim()

  // `_edited.jpg` is a real file in the archive, and trimming it leaves
  // nothing. A blank tile in a grid of 183 is unpickable, so the raw name wins
  // over a tidy one that says nothing.
  return trimmed || stem || fileName
}
